import React, { useState, useEffect, useRef } from "react";
import { ProgressBar } from "../ui/ProgressBar";
import { Tag } from "../ui/Tag";
import clsx from "clsx";
import highlightWiseMate from "../hooks/highlightWord";
import { renderHTML } from "../hooks/htmlToText";
import { useTranslation } from "react-i18next";

export const Benefits = () => {
  const { t } = useTranslation();
  const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const timerDuration = 5000;
  const descriptionContainerRef = useRef<HTMLDivElement | null>(null);
  const benefitsRef = useRef<HTMLDivElement | null>(null);
  const benefits = t("benefits.items", { returnObjects: true }) as {
    title: string;
    description: string;
  }[];

  useEffect(() => {
    const handleScroll = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      });
    };

    const observer = new IntersectionObserver(handleScroll, {
      threshold: 0.1,
    });

    if (benefitsRef.current) {
      observer.observe(benefitsRef.current);
    }

    return () => {
      if (benefitsRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(benefitsRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentBenefitIndex(
            (prevIndex) => (prevIndex + 1) % benefits.length
          );
          return 0;
        }
        return prev + 1;
      });
    }, timerDuration / 100);

    return () => clearInterval(progressInterval);
  }, [benefits.length, isVisible]);

  useEffect(() => {
    const container = descriptionContainerRef.current;
    const currentDescription = document.getElementById(
      currentBenefitIndex.toString()
    );
    if (container && currentDescription) {
      const containerHeight = container.clientHeight;
      const scrollToPosition =
        currentDescription.offsetTop -
        containerHeight / 2 +
        currentDescription.clientHeight / 2;
      container.scrollTo({
        top: Math.max(scrollToPosition, 0),
        behavior: "smooth",
      });
    }
  }, [currentBenefitIndex]);

  return (
    <div
      ref={benefitsRef}
      id="benefits"
      className="justify-center lg:max-w-[1100px] mx-auto w-[90%] md:my-[94px]"
    >
      <div className="flex justify-between items-center md:items-start text-center flex-col md:flex-row md:text-left gap-6">
        <div className="lg:py-4-res">
          <Tag tag={t("tags.benefits")} />
        </div>
        <p className="md:w-[55%] text-primary-700 text-[32px] lg:text-[40px] font-600 md:tracking-[-1.2px]">
          {highlightWiseMate(t("benefits.title"), "font-600")}
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:justify-evenly lg:justify-between items-center mt-[64px] lg:mt-[56px]">
        <div className="flex flex-col mx-auto items-baseline gap-6 w-[98%] md:w-[45%]">
          {benefits.map((benefit, index) => (
            <div key={index} className="mb-4 w-fit">
              <p
                onClick={() => {
                  setCurrentBenefitIndex(index);
                  setProgress(0);
                }}
                className={clsx(
                  "text-[16px] lg:text-[20px] font-600 cursor-pointer",
                  index === currentBenefitIndex
                    ? "text-primary-200"
                    : "text-primary-700"
                )}
              >
                {benefit.title}
              </p>
              {index === currentBenefitIndex && (
                <>
                  <ProgressBar progress={progress} />
                  <p className="text-primary-700 text-[18px] font-500 mt-[20px] md:mt-[0px] md:hidden">
                    {highlightWiseMate(benefit.description, "font-500")}
                  </p>
                </>
              )}
            </div>
          ))}
        </div>

        <div
          ref={descriptionContainerRef}
          style={{ scrollbarWidth: "none" }}
          className="w-[90%] md:w-[55%] h-[250px] overflow-y-auto relative mt-4 hidden md:block"
        >
          <div className="flex flex-col md:gap-[40px] gap-[60px] md:ml-[20px] lg:ml-[0px]">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                id={index.toString()}
                className={`transition-opacity duration-500 ease-in-out mb-4 h-auto text-start ${
                  index === currentBenefitIndex
                    ? "opacity-100"
                    : "opacity-50 blur-sm"
                }`}
                style={{
                  filter:
                    index === currentBenefitIndex ? "none" : "blur(1.5px)",
                }}
              >
                <div className="text-primary-700 text-[18px] font-500 items-baseline">
                  <p>{renderHTML(benefit.description)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
